import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QUERY_PARAMS_KEY } from "../../constants/keys";
import {
  confirmEmailChange,
  confirmStaffParentEmailChange,
} from "../../services/auth.service";

type ConfirmationState =
  | "loading"
  | "success"
  | "invalid"
  | "expired"
  | "already-used"
  | "unauthorized"
  | "email-in-use"
  | "conflict"
  | "error";

function mapConfirmationError(
  status: number,
  error: string,
): ConfirmationState {
  const normalizedError = (error || "").toLowerCase();
  if (status === 410) return "expired";
  if (status === 403) return "unauthorized";
  if (status === 400) return "invalid";
  if (
    status === 409 &&
    (normalizedError === "user_already_exists" ||
      normalizedError.includes("taken") ||
      normalizedError.includes("already in use"))
  )
    return "email-in-use";
  if (status === 409 && normalizedError.includes("already been used"))
    return "already-used";
  if (status === 409) return "conflict";
  return "error";
}

const STATE_CONTENT: Record<
  Exclude<ConfirmationState, "loading">,
  { title: string; body: string }
> = {
  success: {
    title: "Email updated",
    body: "Your email address has been updated successfully. You will be redirected to sign in shortly.",
  },
  expired: {
    title: "This link has expired",
    body: "Email-change links are valid for one hour. Ask an authorized administrator to send a new verification link.",
  },
  "already-used": {
    title: "This link has already been used",
    body: "The email-change request has already been completed or replaced.",
  },
  invalid: {
    title: "This link is invalid",
    body: "The verification link is incomplete or no longer valid.",
  },
  unauthorized: {
    title: "This request can no longer be completed",
    body: "The administrator who requested this change no longer has permission to update this user.",
  },
  "email-in-use": {
    title: "This email is no longer available",
    body: "Another account is already using this email address.",
  },
  conflict: {
    title: "This request can no longer be completed",
    body: "The email-change request could not be completed due to a conflict. Please ask an administrator to resend the request.",
  },
  error: {
    title: "Something went wrong",
    body: "An unexpected error occurred. Please try again or contact support.",
  },
};

const ChangeEmailConfirmation: React.FC = () => {
  const [state, setState] = useState<ConfirmationState>("loading");
  const [updatedEmail, setUpdatedEmail] = useState<string>("");
  const navigate = useNavigate();
  const hasConfirmedRef = useRef(false);

  useEffect(() => {
    // The reset token is single-use; React.StrictMode double-invokes
    // effects in development, so guard against firing the confirm call twice.
    if (hasConfirmedRef.current) return;
    hasConfirmedRef.current = true;

    const url = new URL(window.location.href);
    const token = url.searchParams.get("token");
    const loginUrl = url.searchParams.get("loginUrl");
    const userType = url.searchParams.get(QUERY_PARAMS_KEY.USER_TYPE);

    if (!token) {
      setState("invalid");
      return;
    }

    const finish = (result: any) => {
      if (result?.ok) {
        setUpdatedEmail((result.data as any)?.email || "");
        setState("success");
        url.searchParams.delete("token");
        window.history.replaceState({}, "", url.toString());

        setTimeout(() => {
          window.location.replace(loginUrl || "/sign-in");
        }, 2000);
      } else {
        setState(
          mapConfirmationError(result?.status ?? 500, result?.error ?? ""),
        );
      }
    };

    // The backend appends user_type=staff|parent to the link for those
    // account types (mirroring the reset-password link convention), so
    // this decides the single confirm call to make — no probing needed.
    const confirmFn =
      userType === "staff" || userType === "parent"
        ? confirmStaffParentEmailChange
        : confirmEmailChange;

    confirmFn(token).then(finish);
  }, []);

  return (
    <div className="flex justify-center items-center min-h-screen py-5 px-3">
      <div>
        <img className="mb-9" src="./images/web-logo.png" alt="web-logo" />

        <div className="bg-sky border border-sky py-5 md:py-9 px-4 md:px-16 rounded-[20px] max-w-[614px]">
          {state === "loading" ? (
            <>
              <h3 className="text-secondary text-xl sm:text-2xl font-semibold mb-4">
                Verifying your email change
              </h3>
              <p className="text-base font-light text-secondaryVariant text-opacity-80">
                Please wait while we confirm your email address update...
              </p>
            </>
          ) : (
            <>
              <h3
                className={`text-xl sm:text-2xl font-semibold mb-4 ${
                  state === "success" ? "text-secondary" : "text-red-600"
                }`}
              >
                {STATE_CONTENT[state].title}
              </h3>
              <p className="text-base font-light text-secondaryVariant text-opacity-80">
                {STATE_CONTENT[state].body}
              </p>
              {state === "success" && updatedEmail && (
                <p className="text-base font-medium text-secondary mt-3">
                  New email: {updatedEmail}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangeEmailConfirmation;
